import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, ArrowRight, Rss, Heart, Users, Settings, Newspaper, FileDown } from 'lucide-react';
import { useFeedsStore, useArticlesStore, useAuthStore } from '../store/useStore';
import { social as socialApi } from '../lib/api';

interface Command {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
}

interface CommandModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CommandModal({ isOpen, onClose }: CommandModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((state) => state.token)!;
  const feeds = useFeedsStore((state) => state.feeds);
  const setSelectedFeedId = useFeedsStore((state) => state.setSelectedFeedId);
  const setFilter = useArticlesStore((state) => state.setFilter);
  const setBucket = useArticlesStore((state) => state.setBucket);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [users, setUsers] = useState<any[]>([]);

  // Load users for search commands
  useEffect(() => {
    if (isOpen) {
      socialApi.getAllUsers(token).then(setUsers).catch(() => setUsers([]));
    }
  }, [isOpen, token]);

  // Context-aware commands based on current page
  const commands = useMemo<Command[]>(() => {
    const baseCommands: Command[] = [
      {
        id: 'go-friends',
        title: 'Go to Friends',
        description: 'Articles shared with you',
        icon: <Users className="w-4 h-4" />,
        action: () => navigate('/friends'),
        keywords: ['shared', 'shares', 'social', 'people'],
      },
      {
        id: 'go-articles',
        title: 'Go to Read · All',
        description: 'Everything in the fresh window',
        icon: <Rss className="w-4 h-4" />,
        action: () => {
          setFilter('all');
          setBucket(null);
          setSelectedFeedId(null);
          navigate('/read');
        },
        keywords: ['articles', 'all', 'feeds', 'home'],
      },
      {
        id: 'go-today',
        title: 'Go to Read · Today',
        description: 'Unread from the last day',
        icon: <Rss className="w-4 h-4" />,
        action: () => {
          setFilter('unread');
          setBucket('today');
          setSelectedFeedId(null);
          navigate('/read');
        },
        keywords: ['unread', 'new', 'articles'],
      },
      {
        id: 'go-liked',
        title: 'Go to Liked Articles',
        description: 'View your liked articles',
        icon: <Heart className="w-4 h-4" />,
        action: () => {
          setFilter('liked');
          setSelectedFeedId(null);
          navigate('/read');
        },
        keywords: ['liked', 'favorites', 'saved'],
      },
      {
        id: 'go-digests',
        title: 'Go to Weekly Digests',
        description: 'View your weekly AI-generated reading digests',
        icon: <Newspaper className="w-4 h-4" />,
        action: () => navigate('/digest'),
        keywords: ['digest', 'digests', 'weekly', 'summary', 'ai'],
      },
      {
        id: 'go-users',
        title: 'Go to Users Directory',
        description: 'Browse all users on this instance',
        icon: <Users className="w-4 h-4" />,
        action: () => navigate('/users'),
        keywords: ['users', 'people', 'directory', 'community', 'members'],
      },
      {
        id: 'go-discover',
        title: 'Discover Feeds',
        description: 'Find new RSS feeds based on popularity and curated starter packs',
        icon: <Search className="w-4 h-4" />,
        action: () => navigate('/discover'),
        keywords: ['discover', 'feeds', 'find', 'explore', 'starter', 'packs', 'popular', 'browse'],
      },
      {
        id: 'go-settings',
        title: 'Go to Settings',
        description: 'Change your account settings',
        icon: <Settings className="w-4 h-4" />,
        action: () => navigate('/settings'),
        keywords: ['settings', 'preferences', 'account', 'profile'],
      },
      {
        id: 'go-export',
        title: 'Export Articles',
        description: 'Export your articles to PDF or EPUB for e-readers',
        icon: <FileDown className="w-4 h-4" />,
        action: () => navigate('/export'),
        keywords: ['export', 'pdf', 'epub', 'download', 'kindle', 'remarkable', 'ereader', 'e-reader'],
      },
    ];

    // Add feed-specific commands
    const feedCommands: Command[] = feeds.map(subscription => ({
      id: `feed-${subscription.feed.id}`,
      title: `Go to ${subscription.title || subscription.feed.title}`,
      description: 'View articles from this feed',
      icon: <Rss className="w-4 h-4" />,
      action: () => {
        setSelectedFeedId(subscription.feed.id);
        setFilter('unread');
        setBucket(null);
        navigate('/read');
      },
      keywords: ['feed', subscription.title || subscription.feed.title, subscription.feed.title],
    }));

    // Add user navigation commands
    const userCommands: Command[] = users.map(user => ({
      id: `user-${user.id}`,
      title: `Go to ${user.displayName || user.username}`,
      description: 'View shared articles from this user',
      icon: <Users className="w-4 h-4" />,
      action: () => navigate(`/user/${user.id}`),
      keywords: ['user', user.username, user.displayName || '', 'profile'],
    }));

    // Context-specific commands based on current page
    const contextCommands: Command[] = [];

    if (location.pathname === '/read') {
      contextCommands.push({
        id: 'search-articles',
        title: 'Search Articles',
        description: 'Search through your articles',
        icon: <Search className="w-4 h-4" />,
        action: () => {
          onClose();
          // Focus search input if it exists
          setTimeout(() => {
            const searchInput = document.querySelector('input[type="search"]') as HTMLInputElement;
            if (searchInput) searchInput.focus();
          }, 100);
        },
        keywords: ['search', 'find', 'articles'],
      });
    }

    if (location.pathname === '/friends') {
      contextCommands.push({
        id: 'search-shares',
        title: 'Search Shared Articles',
        description: 'Search through shared articles',
        icon: <Search className="w-4 h-4" />,
        action: () => {
          onClose();
          setTimeout(() => {
            const searchInput = document.querySelector('input[type="search"]') as HTMLInputElement;
            if (searchInput) searchInput.focus();
          }, 100);
        },
        keywords: ['search', 'find', 'shared', 'shares'],
      });
    }

    return [...contextCommands, ...baseCommands, ...feedCommands, ...userCommands];
  }, [feeds, users, location.pathname, navigate, setFilter, setBucket, setSelectedFeedId, onClose]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) return commands.slice(0, 10); // Show top 10 by default

    const lowercaseQuery = query.toLowerCase();
    return commands
      .filter(command =>
        command.title.toLowerCase().includes(lowercaseQuery) ||
        command.description.toLowerCase().includes(lowercaseQuery) ||
        command.keywords.some(keyword => keyword.toLowerCase().includes(lowercaseQuery))
      )
      .slice(0, 10);
  }, [commands, query]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            onClose();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onClose]);

  // Reset selection when commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Reset query when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-24 z-50 backdrop-animate" onClick={onClose}>
      <div
        className="bg-canvas border border-edge w-full max-w-lg mx-4 modal-animate"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Search and commands"
      >
        <div className="flex items-center border-b border-edge">
          <Search className="w-4 h-4 text-ink-muted ml-4" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search feeds, people, commands"
            className="flex-1 h-12 px-3 bg-transparent text-ink placeholder:text-ink-faint focus:outline-none"
            autoFocus
          />
          <kbd className="mr-4">esc</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-6 kicker">No matches</div>
          ) : (
            filteredCommands.map((command, index) => (
              <button
                key={command.id}
                onClick={() => {
                  command.action();
                  onClose();
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-edge-subtle last:border-b-0 border-l-2 ${
                  index === selectedIndex ? 'bg-surface-sunken border-l-accent' : 'border-l-transparent hover:bg-surface-sunken'
                }`}
              >
                <div className="flex-shrink-0 text-ink-muted">
                  {command.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink truncate">
                    {command.title}
                  </div>
                  <div className="kicker normal-case tracking-normal truncate">
                    {command.description}
                  </div>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-ink-faint" />
              </button>
            ))
          )}
        </div>

        <div className="px-4 py-2 border-t border-edge flex items-center justify-between kicker">
          <span><kbd>↑</kbd> <kbd>↓</kbd> move · <kbd>↵</kbd> go</span>
          <span><kbd>j</kbd> <kbd>k</kbd> in lists</span>
        </div>
      </div>
    </div>
  );
}
